import { useApolloClient } from '@apollo/client'
import React from 'react'
import { BookingsCountDocument, BookingsPaginatedDocument, OrderDirection } from '@typings/graphql'

import { getYearDiscoveryRange } from './dateRangeUtils'

const getEarliestBookingYear = async (client: ReturnType<typeof useApolloClient>): Promise<number> => {
  const now = new Date()
  const { data } = await client.query({
    query: BookingsPaginatedDocument,
    variables: {
      data: {
        limit: 1,
        offset: 0,
        orderBy: {
          field: 'createdAt',
          direction: OrderDirection.Asc
        }
      }
    },
    fetchPolicy: 'network-only'
  })

  const createdAt = data?.bookingsPaginated?.nodes?.[0]?.createdAt
  if (!createdAt) {
    return now.getFullYear()
  }

  return new Date(createdAt).getFullYear()
}

export const useAvailableBookingYears = () => {
  const client = useApolloClient()
  const [years, setYears] = React.useState<number[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let cancelled = false

    const loadYears = async () => {
      setLoading(true)

      const now = new Date()
      const currentYear = now.getFullYear()
      const earliestYear = await getEarliestBookingYear(client)
      const startYear = Math.min(earliestYear, currentYear)

      const candidateYears = Array.from(
        { length: currentYear - startYear + 1 },
        (_, index) => startYear + index
      )

      const yearsWithBookings = await Promise.all(
        candidateYears.map(async (year) => {
          const [startDate, endDate] = getYearDiscoveryRange(year, now)
          const { data } = await client.query({
            query: BookingsCountDocument,
            variables: {
              data: { startDate, endDate }
            },
            fetchPolicy: 'network-only'
          })

          const total = data?.bookingsCount?.reduce((sum: number, tour: { count: number }) => sum + tour.count, 0) ?? 0
          return total > 0 ? year : null
        })
      )

      if (!cancelled) {
        setYears(
          yearsWithBookings
            .filter((year): year is number => year !== null)
            .sort((a, b) => b - a)
        )
        setLoading(false)
      }
    }

    void loadYears()

    return () => {
      cancelled = true
    }
  }, [client])

  return { years, loading }
}
